import { DG_STORE_CONFIG, buildWhatsAppUrl, DG_WHATSAPP_CONFIG } from '@dg-embalajes/commerce';
import {
  DEFAULT_PLOT_MATERIAL_ID,
  DEFAULT_STICKER_PRESET_CODE,
  DEFAULT_TUBE_PRESET_CODE,
  PLOT_MATERIALS,
  STICKER_PRESETS,
  TUBE_PRESETS,
  calculateEffectiveDevelopmentMm,
  createCustomStickerProductV1,
  createPresetStickerProductV1,
  formatStickerCmFromMm,
  getPlotMaterial,
  getStickerPreset,
  getTubePreset,
  parseHeightCm,
  resolveTubeProduct,
} from '@dg-embalajes/product-domain';
import { ExternalLink, MessageCircle, Package, Search, Shapes } from 'lucide-react';
import { useMemo, useState } from 'react';
import { APP_VERSION, COPY } from '@/content/copy';
import type { EditorPort } from '@/ports/editor';
import { SegmentedControl } from './SegmentedControl';

type ProductMode = 'tube' | 'sticker';
type StickerShape = 'circle' | 'square' | 'rect';

export function SetupScreen({ editor }: { readonly editor: EditorPort }) {
  const [mode, setMode] = useState<ProductMode>('tube');
  const [presetCode, setPresetCode] = useState<string>(DEFAULT_TUBE_PRESET_CODE);
  const [materialId, setMaterialId] = useState<string>(DEFAULT_PLOT_MATERIAL_ID);
  const [height, setHeight] = useState('15');
  const [error, setError] = useState<string | null>(null);
  const [stickerGroup, setStickerGroup] = useState('Hojalata');
  const [stickerSearch, setStickerSearch] = useState('');
  const [stickerCode, setStickerCode] = useState<string>(DEFAULT_STICKER_PRESET_CODE);
  const [customSticker, setCustomSticker] = useState(false);
  const [stickerShape, setStickerShape] = useState<StickerShape>('circle');
  const [customWidth, setCustomWidth] = useState('5,8');
  const [customHeight, setCustomHeight] = useState('5,8');

  const tubePreset = getTubePreset(presetCode);
  const material = getPlotMaterial(materialId);
  const heightResult = parseHeightCm(height);
  const effectiveDevelopment = calculateEffectiveDevelopmentMm(tubePreset, material);
  const customStickerResult = createCustomStickerProductV1({
    shape: stickerShape,
    widthInput: customWidth,
    heightInput: customHeight,
  });
  const filteredStickers = useMemo(() => {
    const query = stickerSearch.trim().toLocaleLowerCase('es');
    return STICKER_PRESETS.filter(
      (preset) =>
        query
          ? `${preset.code} ${preset.lid} ${preset.group}`.toLocaleLowerCase('es').includes(query)
          : preset.group === stickerGroup,
    );
  }, [stickerGroup, stickerSearch]);

  const switchMode = (next: ProductMode) => {
    setMode(next);
    setError(null);
  };

  const createTube = async () => {
    const result = resolveTubeProduct({ presetCode, materialId, heightInput: height });
    if (!result.ok) {
      setError(
        result.error.code === 'format' || result.error.code === 'required'
          ? 'Ingresá un alto válido. Ejemplo: 15 o 27,5.'
          : 'El alto permitido es de 3 cm a 60 cm.',
      );
      return;
    }
    setError(null);
    await editor.createProject(result.value);
  };

  const createSticker = async () => {
    if (!customSticker) {
      setError(null);
      await editor.createProject(createPresetStickerProductV1(getStickerPreset(stickerCode)));
      return;
    }
    const result = createCustomStickerProductV1({
      shape: stickerShape,
      widthInput: customWidth,
      heightInput: customHeight,
    });
    if (!result.ok) {
      setError('Ingresá una medida válida para el sticker.');
      return;
    }
    setError(null);
    await editor.createProject(result.value);
  };

  const supportLinks = (
    <nav className="setup-support-links" aria-label="Ayuda comercial">
      <a href={DG_STORE_CONFIG.url} target="_blank" rel="noreferrer">
        <ExternalLink aria-hidden="true" size={18} /> {COPY.store}
      </a>
      <a
        className="utility-whatsapp"
        href={buildWhatsAppUrl(DG_WHATSAPP_CONFIG.messages.general)}
        target="_blank"
        rel="noreferrer"
      >
        <MessageCircle aria-hidden="true" size={18} /> WhatsApp
      </a>
    </nav>
  );

  return (
    <div className="setup-screen">
      <header className="setup-topbar">
        <div className="setup-brand">
          <img src="/assets/icons/icon-192.png" alt="DG embalajes" />
          <div>
            <strong>DG embalajes</strong>
            <span>{APP_VERSION}</span>
          </div>
        </div>
      </header>

      <main className="setup-main">
        <section className="setup-intro" aria-labelledby="setup-title">
          <div>
            <h1 id="setup-title">{COPY.title}</h1>
            <p>{COPY.description}</p>
          </div>
          <div className="setup-mode-block">
            <strong>{COPY.chooseProduct}</strong>
            <span>{COPY.chooseProductHelp}</span>
            <SegmentedControl
              label="Seleccionar herramienta"
              value={mode}
              options={[
                { value: 'tube', label: COPY.tubeMode },
                { value: 'sticker', label: COPY.stickerMode },
              ]}
              onChange={switchMode}
            />
          </div>
        </section>

        {mode === 'tube' ? (
          <section className="setup-workflow" aria-label="Configurar Ploteo de tubos">
            <div className="setup-fields">
              <fieldset className="field-section">
                <legend>Material del ploteo</legend>
                <p>Papel fotográfico queda seleccionado por defecto. Para lona, el sistema ajusta automáticamente el desarrollo según la medida.</p>
                <div className="material-grid">
                  {PLOT_MATERIALS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={`choice-card ${materialId === item.id ? 'is-selected' : ''}`}
                      aria-pressed={materialId === item.id}
                      onClick={() => setMaterialId(item.id)}
                    >
                      <span>{item.label}</span>
                      <small>{item.id === 'photo-paper' ? 'Medida base' : 'Ajuste automático para lona'}</small>
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="field-section">
                <legend>{COPY.chooseDiameter}</legend>
                <p>{COPY.diameterHelp}</p>
                <div className="tube-grid">
                  {TUBE_PRESETS.map((preset) => (
                    <button
                      key={preset.code}
                      type="button"
                      className={`choice-card ${presetCode === preset.code ? 'is-selected' : ''}`}
                      aria-pressed={presetCode === preset.code}
                      onClick={() => setPresetCode(preset.code)}
                    >
                      <span>{preset.diameterMm} mm</span>
                      <small>{preset.code} · desarrollo {calculateEffectiveDevelopmentMm(preset, material)} mm</small>
                    </button>
                  ))}
                </div>
              </fieldset>

            </div>

            <aside className="live-summary" aria-live="polite">
              <div className="live-summary__heading">
                <Package aria-hidden="true" />
                <div><span>Medida elegida</span><strong>Tubo {tubePreset.diameterMm} mm</strong></div>
              </div>
              <div className="live-height-field">
                <label htmlFor="tube-height"><strong>{COPY.enterHeight}</strong></label>
                <div className="unit-input">
                  <input
                    id="tube-height"
                    inputMode="decimal"
                    value={height}
                    placeholder={COPY.heightPlaceholder}
                    aria-invalid={Boolean(error)}
                    onChange={(event) => {
                      setHeight(event.target.value);
                      setError(null);
                    }}
                  />
                  <span>cm</span>
                </div>
                <small>{COPY.heightHelp}</small>
              </div>
              <dl>
                <div><dt>Código</dt><dd>{tubePreset.code}</dd></div>
                <div><dt>Material</dt><dd>{material.label}</dd></div>
                <div><dt>Alto envase</dt><dd>{heightResult.ok ? `${heightResult.millimeters} mm` : 'Revisar'}</dd></div>
                <div className="summary-total"><dt>Desarrollo del archivo</dt><dd>{effectiveDevelopment} mm</dd></div>
              </dl>
              <div className="measure-explainer">
                <span><strong>ALTO</strong> es la altura del envase.</span>
                <span><strong>DESARROLLO</strong> es la vuelta alrededor del tubo.</span>
              </div>
              {error ? <p className="form-error" role="alert">{error}</p> : null}
              <button className="primary-action" type="button" onClick={createTube}>{COPY.createWorkArea}</button>
              <p className="setup-help">{COPY.noDesignKnowledge}</p>
              {supportLinks}
            </aside>
          </section>
        ) : (
          <section className="setup-workflow" aria-label="Configurar Sticker">
            <div className="setup-fields">
              <fieldset className="field-section">
                <legend>2. Elegí la medida del sticker</legend>
                <p>Seleccioná el código de tapa/envase. La guía de pantalla sirve para ubicar el diseño. El PDF incluye una línea fina negra de corte para revisión.</p>
                <SegmentedControl
                  label="Grupo de tapa"
                  value={stickerGroup}
                  options={['Hojalata', 'ABS', 'Plásticas'].map((value) => ({ value, label: value }))}
                  onChange={(value) => {
                    setCustomSticker(false);
                    setStickerGroup(value);
                    const first = STICKER_PRESETS.find((preset) => preset.group === value);
                    if (first) setStickerCode(first.code);
                  }}
                />
                <label className="search-field">
                  <Search aria-hidden="true" size={18} />
                  <span className="sr-only">Buscar por código, tapa o grupo</span>
                  <input value={stickerSearch} placeholder="Ej: 83hn, 2ap, 4B..." onChange={(event) => setStickerSearch(event.target.value)} />
                </label>
                <div className="sticker-grid">
                  {filteredStickers.map((preset) => (
                    <button
                      key={preset.code}
                      type="button"
                      className={`choice-card ${!customSticker && stickerCode === preset.code ? 'is-selected' : ''}`}
                      onClick={() => {
                        setCustomSticker(false);
                        setStickerGroup(preset.group);
                        setStickerCode(preset.code);
                      }}
                    >
                      <span>{preset.code}</span>
                      <small>{preset.lid} · {formatStickerCmFromMm(preset.widthMm)} cm máx.</small>
                    </button>
                  ))}
                </div>
              </fieldset>

              <fieldset className="field-section">
                <legend>3. O creá una medida personalizada</legend>
                <button type="button" className={`custom-toggle ${customSticker ? 'is-selected' : ''}`} onClick={() => setCustomSticker(true)}>
                  <Shapes aria-hidden="true" /> Personalizado · medida propia
                </button>
                {customSticker ? (
                  <div className="custom-sticker-fields">
                    <SegmentedControl
                      label="Forma"
                      value={stickerShape}
                      options={[
                        { value: 'circle', label: 'Círculo' },
                        { value: 'square', label: 'Cuadrado' },
                        { value: 'rect', label: 'Rectángulo' },
                      ]}
                      onChange={(value) => { setStickerShape(value); setError(null); }}
                    />
                    <label>Ancho / diámetro<div className="unit-input"><input inputMode="decimal" value={customWidth} onChange={(event) => { setCustomWidth(event.target.value); setError(null); }} /><span>cm</span></div></label>
                    {stickerShape === 'rect' ? <label>Alto<div className="unit-input"><input inputMode="decimal" value={customHeight} onChange={(event) => { setCustomHeight(event.target.value); setError(null); }} /><span>cm</span></div></label> : null}
                  </div>
                ) : null}
              </fieldset>
            </div>

            <aside className="live-summary" aria-live="polite">
              <div className="live-summary__heading"><Shapes aria-hidden="true" /><div><span>Mesa de sticker</span><strong>{customSticker ? 'Personalizado' : `${getStickerPreset(stickerCode).code} · ${getStickerPreset(stickerCode).group}`}</strong></div></div>
              <dl>
                <div><dt>Forma</dt><dd>{customSticker ? stickerShape === 'circle' ? 'Círculo' : stickerShape === 'square' ? 'Cuadrado' : 'Rectángulo' : 'Círculo'}</dd></div>
                <div className="summary-total"><dt>PDF real</dt><dd>{customSticker
                  ? customStickerResult.ok
                    ? `${customStickerResult.value.widthMm} × ${customStickerResult.value.heightMm} mm`
                    : 'Revisar medidas'
                  : `${getStickerPreset(stickerCode).widthMm} × ${getStickerPreset(stickerCode).heightMm} mm`}</dd></div>
              </dl>
              {error ? <p className="form-error" role="alert">{error}</p> : null}
              <button className="primary-action" type="button" onClick={createSticker}>Crear mesa de sticker</button>
              <p className="setup-help">El área corresponde al máximo recomendado para pegar sobre la tapa. Si no sabés qué código tenés, consultanos por WhatsApp antes de armar el archivo.</p>
              {supportLinks}
            </aside>
          </section>
        )}
      </main>
    </div>
  );
}
